import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens";
import UnpairedPayment from "entities/unpairedPayment";
import { computed } from "mobx";
import PaymentsPairingRepository from "repositories/paymentsPairingRepository";
import EventBus, { paymentPaired, paymentRefunded } from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import ConfirmationService from "services/confirmationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import UnpairedPaymentStatus from "entities/unpairedPaymentStatus";
import { allowToRefund } from "../rights";
import UnpairedPaymentListViewModel from "../unpairedPaymentListViewModel";
import PairingByPaymentDetailViewModel from "./pairingByPaymentDetailViewModel";

const navigationName = "payments";

@Router.registerRoute({ name: Router.Self, route: navigationName, children: [PairingByPaymentDetailViewModel] })
export default class PairingByPaymentListViewModel extends UnpairedPaymentListViewModel<PairingByPaymentDetailViewModel> {
  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: PaymentsPairingRepository,
    protected detailViewModelFactory: ReturnType<typeof PairingByPaymentDetailViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize() {
    this.unsubscriptions.push(
      this.eventBus.subscribe(paymentPaired, this.onPaymentPaired),
      this.eventBus.subscribe(paymentRefunded, this.onPaymentRefunded)
    );
    return super.onInitialize();
  }

  @computed
  get name() {
    if (!this.isInitialized) {
      return this.translate("payments");
    } else {
      return this.translate("paymentsCount").replace("%count%", this.currentPaging?.totalItems.toString() || "0");
    }
  }

  @bound
  async onPaymentPaired(event: ReturnType<typeof paymentPaired>) {
    if (this.activeChild) {
      this.closeChild(this.activeChild);
    }
    await this.loadData();
    const pairedItem = this.items.find(item => item.id === event.payload.payment.id);

    // Pairing is ansychronous task and can take a while, sometimes is already paired payment presented at list for a moment
    if (pairedItem) {
      pairedItem.isPaired = true;
    }
  }

  @bound
  async onCreateRefundClick(payment: UnpairedPayment) {
    if (!this.allowToRefund) {
      this.notificationService.addNotAllowedError();
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmRefund.title"), {
      confirmButton: { text: this.translate("confirmRefund.confirm") },
    });

    if (confirmed) {
      try {
        await this.repository.refundPayment(payment);
        this.eventBus.publish(paymentRefunded({ payment }));
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  onPaymentRefunded(event: ReturnType<typeof paymentRefunded>) {
    const payment = event.payload.payment;

    this.notificationService.add({ title: this.translate("refunded"), type: NotificationType.SUCCESS });
    const item = this.items.find(item => item.id === payment.id);
    if (item) {
      item.status = UnpairedPaymentStatus.RefundCreated;
    }
  }

  @computed
  get allowToRefund() {
    return allowToRefund(this.securityService);
  }
}
